import { useState, useCallback, useRef, useEffect } from "react";
import { sendOtp, verifyOtp, getNextUid } from "../utils/api";

const SUBMIT_KEY = "contactPopupSubmittedAt";
const TTL_MS = 24 * 60 * 60 * 1000;

function getDomainWithMainPath() {
  const domain = window.location.hostname.replace("www.", "");
  const segment = window.location.pathname.split("/").filter(Boolean)[0];
  return segment ? `${domain}/${segment}` : domain;
}

export function useLeadForm({ projectId, formDefaults = {} }) {
  const [modal, setModal] = useState("closed");
  const [c2aEvent, setC2aEvent] = useState("PopUp");

  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
  });

  const [loading, setLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [resendCountdown, setResendCountdown] = useState(0);

  const uidRef = useRef(null);
  const autoPopupRef = useRef(null);
  const countdownRef = useRef(null);

  // 🔥 NEW: popup counter
  const popupCountRef = useRef(0);

  // ============================
  // ✅ SAFE ACCESSORS
  // ============================
  const getProjectName = useCallback(
    () => formDefaults?.project || "",
    [formDefaults]
  );

  const getLocation = useCallback(
    () => formDefaults?.location || "",
    [formDefaults]
  );

  // ============================
  // ✅ CHECK 24h SUBMISSION
  // ============================
  const isAlreadySubmitted = useCallback(() => {
    try {
      const ts = localStorage.getItem(SUBMIT_KEY);
      if (!ts) return false;
      return Date.now() - Number(ts) < TTL_MS;
    } catch {
      return false;
    }
  }, []);

  // ============================
  // 🔥 POPUP DELAY LOGIC
  // ============================
  const getNextPopupDelay = () => {
    popupCountRef.current++;

    if (popupCountRef.current === 1) return 3000;
    if (popupCountRef.current === 2) return 5000;
    return 10000;
  };

  const scheduleAutoPopup = useCallback(() => {
    clearTimeout(autoPopupRef.current);

    const delay = getNextPopupDelay();

    autoPopupRef.current = setTimeout(() => {
      setModal((prev) => (prev === "closed" ? "contact" : prev));
    }, delay);
  }, []);

  // ============================
  // ✅ OPEN POPUP (manual)
  // ============================
  const openPopup = useCallback(
    (event = "PopUp") => {
      setC2aEvent(event);

      if (isAlreadySubmitted()) {
        setModal("already");
      } else {
        setModal("contact");
      }
    },
    [isAlreadySubmitted]
  );

  // ============================
  // ✅ CLOSE POPUP + RESCHEDULE
  // ============================
  const closeAll = useCallback(() => {
    setModal("closed");

    if (!isAlreadySubmitted()) {
      scheduleAutoPopup(); // 🔥 schedule next popup
    }
  }, [isAlreadySubmitted, scheduleAutoPopup]);

  // ============================
  // 🔥 INITIAL AUTO POPUP
  // ============================
  useEffect(() => {
    if (!formDefaults?.project || !formDefaults?.location) return;
    if (isAlreadySubmitted()) return;

    scheduleAutoPopup();

    return () => clearTimeout(autoPopupRef.current);
  }, [formDefaults, isAlreadySubmitted, scheduleAutoPopup]);

  // ============================
  // ⏳ RESEND COUNTDOWN
  // ============================
  const startResendCountdown = () => {
    clearInterval(countdownRef.current);
    setResendCountdown(60);

    countdownRef.current = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // ============================
  // 📩 SEND OTP
  // ============================
  const handleSendOtp = useCallback(
    async ({ fullName, phone, email }) => {
      setLoading(true);
      setOtpError("");

      try {
        if (!uidRef.current) {
          uidRef.current = await getNextUid();
        }

        const uid = uidRef.current;

        setFormData({
          fullName,
          phone,
          email: email || "",
          uniqueId: uid,
        });

        const payload = {
          phone,
          fullName,
          projectName: getProjectName(),
          location: getLocation(),
          domainName: getDomainWithMainPath(),
          uniqueId: uid,
          c2aEvent: c2aEvent || "Form Submit",
        };

        console.log("📤 SEND OTP:", payload);

        const result = await sendOtp(payload);
        if (result?.error) throw new Error(result.error);

        setModal("otp");
        startResendCountdown();
      } catch (e) {
        setOtpError(e.message || "Failed to send OTP");
      } finally {
        setLoading(false);
      }
    },
    [getProjectName, getLocation, c2aEvent]
  );

  // ============================
  // 🔐 VERIFY OTP
  // ============================
  const handleVerifyOtp = useCallback(
    async (otp) => {
      setLoading(true);
      setOtpError("");

      try {
        const { fullName, phone, email, uniqueId } = formData;

        const payload = {
          phone,
          otp,
          fullName,
          email,
          projectName: getProjectName(),
          location: getLocation(),
          domainName: getDomainWithMainPath(),
          uniqueId,
          c2aEvent: c2aEvent || "Form Submit",
        };

        console.log("📥 VERIFY OTP:", payload);

        const result = await verifyOtp(payload);

        if (result?.error) {
          setOtpError(result.error);
          return;
        }

        localStorage.setItem(SUBMIT_KEY, Date.now().toString());
        uidRef.current = null;

        setModal("thankyou");

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "thankYouPopupVisible",
          lead_name: fullName,
          lead_phone: phone,
          project_name: getProjectName(),
          lead_source: c2aEvent || "Form Submit",
        });

        setTimeout(() => {
          setModal("closed");
        }, 4000);
      } catch (e) {
        setOtpError(e.message || "Verification failed");
      } finally {
        setLoading(false);
      }
    },
    [formData, getProjectName, getLocation, c2aEvent]
  );

  // ============================
  // 🔁 RESEND OTP
  // ============================
  const handleResendOtp = useCallback(async () => {
    if (resendCountdown > 0) return;
    await handleSendOtp(formData);
  }, [handleSendOtp, formData, resendCountdown]);

  // ============================
  // 🧹 CLEANUP
  // ============================
  useEffect(() => {
    return () => {
      clearTimeout(autoPopupRef.current);
      clearInterval(countdownRef.current);
    };
  }, []);

  return {
    modal,
    loading,
    otpError,
    resendCountdown,
    formData,
    c2aEvent,
    formDefaults,

    openPopup,
    closeAll,
    handleSendOtp,
    handleVerifyOtp,
    handleResendOtp,
    setFormData,
  };
}